import { Text, View } from '@react-pdf/renderer';
import type { ReactElement } from 'react';

import type { Quote } from '@core/types/api/Quote/Quote';
import { randomUUID } from '@core/utils/uuid';

import { PdfHeader } from '../../../components/PdfHeader';
import type { PdfImageProps } from '../../../components/PdfImage';
import {
  type PdfColumnConfig,
  PdfTable,
  Styles,
  pdfColumnFromConfig,
} from '../../../components/PdfTable';

const formatLeftValueForPdf = (value: string): ReactElement => (
  <View style={{ textAlign: 'left', paddingRight: '8pt' }}>
    <Text style={[Styles.tableCell, { padding: '0' }]}>{value}</Text>
  </View>
);

const formatRightValueForPdf = (value: string): ReactElement => (
  <View style={{ textAlign: 'right', paddingLeft: '8pt' }}>
    <Text style={[Styles.tableCell, { padding: '0' }]}>{value}</Text>
  </View>
);

const formatElementForPdf = (value: ReactElement): ReactElement => value;

type InfoRow = {
  id: string;
  leftValue: string | null;
  rightValue: string | null;
};

type HeaderRow = {
  id: string;
  leftValue: ReactElement;
  rightValue: ReactElement;
};

type PdfHeaderTableProps = {
  quote: Quote;
  logo: PdfImageProps;
  locale: string;
  infoTableProps?: {
    leftValue?: Pick<PdfColumnConfig<HeaderRow>, 'widthOverride'>;
    rightValue?: Pick<PdfColumnConfig<HeaderRow>, 'widthOverride'>;
  };
};

export const PdfHeaderTable = ({
  quote,
  logo,
  locale,
  infoTableProps,
}: PdfHeaderTableProps): ReactElement => {
  const logoImg = (
    <View style={{ alignItems: 'center' }}>
      <PdfHeader image={logo} />
    </View>
  );

  const quoteCompanyAddressEmpty = (quote.company?.address?.basicDisplay() ?? '') === '';
  const infoRows: InfoRow[] = [
    {
      id: randomUUID(),
      leftValue: quote.company !== null ? `Customer: ${quote.company.name}` : '',
      rightValue: `Project: ${quote.project.name}`,
    },
    {
      id: randomUUID(),
      leftValue: quoteCompanyAddressEmpty
        ? ''
        : `Address: ${quote.company?.address?.basicDisplay()}`,
      rightValue: `Location: ${quote.project.address.basicDisplay()}`,
    },
    {
      id: randomUUID(),
      leftValue: ((): string | null => {
        const secondaryAddress =
          quote.company === null ? null : quote.company.address.basicStateDisplay();
        return quoteCompanyAddressEmpty && secondaryAddress !== null
          ? `Address: ${secondaryAddress}`
          : secondaryAddress;
      })(),
      rightValue: quote.project.address.basicStateDisplay(),
    },
    {
      id: randomUUID(),
      leftValue: [quote.contacts[0]?.fullName(), quote.contacts[0]?.phoneNumber]
        .filter((s) => s !== undefined)
        .join(' '),
      rightValue: null,
    },
    {
      id: randomUUID(),
      leftValue: null,
      rightValue: quote.creationDate === null ? null : quote.creationDateDisplay(locale),
    },
    {
      id: randomUUID(),
      leftValue: null,
      rightValue: quote.expirationDate === null ? null : quote.expirationDateDisplay(locale),
    },
    {
      id: randomUUID(),
      leftValue: null,
      rightValue:
        quote.revisionDate === null ? null : `Revised ${quote.revisionDateDisplay(locale)}`,
    },
  ];

  const infoCols: PdfColumnConfig<InfoRow>[] = [
    {
      id: 'leftValue',
      label: '',
      type: 'string',
      isDisplayed: false,
      formatValueForPdf: formatLeftValueForPdf,
    },
    {
      id: 'rightValue',
      label: '',
      type: 'string',
      isDisplayed: false,
      formatValueForPdf: formatRightValueForPdf,
    },
  ];

  const infoTable = (
    <PdfTable columns={infoCols.map(pdfColumnFromConfig)} rows={infoRows} cellPadding='tight' />
  );

  const headerRows: HeaderRow[] = [
    {
      id: randomUUID(),
      leftValue: logoImg,
      rightValue: infoTable,
    },
  ];

  const headerCols: PdfColumnConfig<HeaderRow>[] = [
    {
      id: 'leftValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      formatValueForPdf: formatElementForPdf as (
        value: ReactElement | string | null,
      ) => ReactElement,
      ...infoTableProps?.leftValue,
    },
    {
      id: 'rightValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      formatValueForPdf: formatElementForPdf as (
        value: ReactElement | string | null,
      ) => ReactElement,
      ...infoTableProps?.rightValue,
    },
  ];

  return (
    <PdfTable columns={headerCols.map(pdfColumnFromConfig)} rows={headerRows} cellPadding='tight' />
  );
};
